import { concatMap } from 'rxjs/operators';

import { ProgramTemplatesBackend } from '@libs/src/backend/program-templates.backend';
import { GroupsBackend } from '@libs/src/groups/groups.backend';

import type { Group } from '@libs/src/models/group.model';
import { PathwayPopulated } from '@libs/src/models/pathway.interface';
import type { ProgramTemplate } from '@libs/src/models/program-template.model';
import type { PathwayPopulatedAdminFragment } from '@libs/src/pathways/pathways.graphql.types';
import { PathwayExperienceTemplateDropDownItem } from '@libs/src/pathways/pathways.types';
import { chunkOperations } from '@libs/src/rxjs/rxjs.helper';
import { defaultOpportunityTemplates } from '@main-client/src/app/opportunity-templates-view/opportunity-template';
import { OpportunityTemplate } from '@main-client/src/app/opportunity-templates-view/opportunity-template.model';

export function getPathwaysLabelGroup(
  labels: OpportunityTemplate['labels'] | ProgramTemplate['labels'],
) {
  return (labels ?? []).find(
    (labelGroup) => 'Pathways' === labelGroup?.label?.display_name,
  );
}

export function attachSuggestedProgramTemplates(
  group: Group,
  pathway: PathwayPopulated,
  programTemplatesBackend: ProgramTemplatesBackend,
) {
  return programTemplatesBackend.v2
    .listProgramTemplates(group._id, { limit: '100', skip: '0' })
    .pipe(
      concatMap(({ programTemplates }) => {
        const templatesToAttach = programTemplates.filter(({ labels }) => {
          const labelGroup = getPathwaysLabelGroup(labels);
          return !!(labelGroup?.label_options ?? []).find(
            (option) =>
              pathway.name?.trim().toLowerCase() ===
              option.display_name?.trim().toLowerCase(),
          );
        });
        return bulkAddPathwayToProgramTemplates(
          templatesToAttach,
          pathway,
          group,
          programTemplatesBackend,
        );
      }),
    );
}

export function addPathwayToProgramTemplate(
  pathway: PathwayPopulated | PathwayPopulatedAdminFragment,
  group: Group,
  programTemplate: Pick<ProgramTemplate, '_id'>,
  programTemplatesBackend: ProgramTemplatesBackend,
) {
  return programTemplatesBackend.v2.patchProgramTemplatePathway(
    group._id,
    programTemplate._id,
    pathway._id,
  );
}

function bulkAddPathwayToProgramTemplates(
  programTemplates: Array<Pick<ProgramTemplate, '_id'>>,
  pathway: PathwayPopulated,
  group: Group,
  programTemplatesBackend: ProgramTemplatesBackend,
) {
  return chunkOperations(
    programTemplates.map((template) =>
      addPathwayToProgramTemplate(
        pathway,
        group,
        template,
        programTemplatesBackend,
      ),
    ),
  );
}

export function attachSuggestedEventTemplates(
  group: Group,
  pathway: PathwayPopulated,
  groupsBackend: GroupsBackend,
) {
  const templatesToAttach: Array<Pick<OpportunityTemplate, '_id'>> =
    Object.entries(defaultOpportunityTemplates)
      .filter(([, templateConfig]) => {
        const template = templateConfig.buildOpportunityTemplate();
        const labelGroup = getPathwaysLabelGroup(template.labels);
        return !!(labelGroup?.label_options ?? []).find(
          (option) =>
            pathway.name?.trim().toLowerCase() ===
            option.display_name?.trim().toLowerCase(),
        );
      })
      .map(([templateName]) => ({ _id: templateName }));
  return bulkAddPathwayToOpportunityTemplates(
    templatesToAttach,
    pathway,
    group,
    groupsBackend,
  );
}

export function addPathwayToOpportunityTemplate(
  pathway: PathwayPopulated | PathwayPopulatedAdminFragment,
  group: Group,
  template: Pick<OpportunityTemplate, '_id'>,
  groupsBackend: GroupsBackend,
) {
  return groupsBackend.v3.patchOpportunityTemplatePathway(
    group._id,
    template._id,
    pathway._id,
  );
}

function bulkAddPathwayToOpportunityTemplates(
  opportunityTemplates: Array<Pick<OpportunityTemplate, '_id'>>,
  pathway: PathwayPopulated,
  group: Group,
  groupsBackend: GroupsBackend,
) {
  return chunkOperations(
    opportunityTemplates.map((template) =>
      addPathwayToOpportunityTemplate(pathway, group, template, groupsBackend),
    ),
  );
}

export function removePathwayFromOpportunityTemplate(
  pathway: PathwayPopulated | PathwayPopulatedAdminFragment,
  group: Group,
  template: PathwayExperienceTemplateDropDownItem,
  groupsBackend: GroupsBackend,
) {
  return groupsBackend.v3.deleteOpportunityTemplatePathway(
    group._id,
    template.document._id,
    pathway._id,
  );
}

export function removePathwayFromProgramTemplate(
  pathway: PathwayPopulated | PathwayPopulatedAdminFragment,
  group: Group,
  template: PathwayExperienceTemplateDropDownItem,
  programTemplatesBackend: ProgramTemplatesBackend,
) {
  return programTemplatesBackend.v2.deleteProgramTemplatePathway(
    group._id,
    template.document._id,
    pathway._id,
  );
}
